import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, Filter, Archive, Sparkles, Bug, Wrench, Rocket, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { notificationsAPI, whatsNewAPI } from '../lib/api';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from 'sonner';

const priorityColors = {
  urgent: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  normal: 'bg-blue-100 text-blue-800',
  low: 'bg-gray-100 text-gray-800',
};

const moduleIcons = {
  travel: '✈️',
  timesheet: '⏱️',
  expense: '💰',
  payroll: '💵',
  leave: '🏖️',
  system: '⚙️',
  user: '👤',
  onboarding: '📋',
  assets: '📦',
  crm: '🤝',
  devops: '🔧',
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'notifications');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('unread');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // What's New state
  const [releases, setReleases] = useState([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [unreadReleases, setUnreadReleases] = useState([]);
  
  // Fetch What's New releases
  const fetchReleases = async () => {
    setReleasesLoading(true);
    try {
      const [releasesRes, unreadRes] = await Promise.all([
        whatsNewAPI.getReleases({ limit: 20 }),
        whatsNewAPI.getUnread()
      ]);
      setReleases(releasesRes.data.releases || []);
      setUnreadReleases(unreadRes.data || []);
    } catch (error) {
      console.error('Failed to fetch releases:', error);
    } finally {
      setReleasesLoading(false);
    }
  };
  
  useEffect(() => {
    if (activeTab === 'whats-new') {
      fetchReleases();
    }
  }, [activeTab]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const fetchNotifications = async (reset = false) => {
    setLoading(true);
    try {
      const params = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        module: moduleFilter !== 'all' ? moduleFilter : undefined,
        page: reset ? 1 : page,
        limit: 20,
      };
      const response = await notificationsAPI.getAll(params);
      const data = response.data;
      setNotifications(reset ? data.notifications : [...notifications, ...data.notifications]);
      setUnreadCount(data.unread_count);
      setHasMore(data.has_more);
      if (reset) setPage(1);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(true);
  }, [statusFilter, moduleFilter]);

  const handleMarkAsRead = async (ids) => {
    try {
      await notificationsAPI.markAsRead(ids);
      setNotifications(prev => 
        prev.map(n => ids.includes(n.id) ? { ...n, status: 'read' } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - ids.length));
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleArchive = async (ids) => {
    try {
      await notificationsAPI.archive(ids);
      setNotifications(prev => prev.filter(n => !ids.includes(n.id)));
      toast.success('Archived');
    } catch (error) {
      toast.error('Failed to archive');
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationsAPI.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleClick = (notification) => {
    if (notification.status === 'unread') {
      handleMarkAsRead([notification.id]);
    }
    if (notification.metadata?.deep_link) {
      navigate(notification.metadata.deep_link);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Notifications</h1>
          <p className="text-muted-foreground">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
          </p>
        </div>
        {activeTab === 'notifications' && unreadCount > 0 && (
          <Button onClick={handleMarkAllAsRead}>
            <CheckCheck className="h-4 w-4 mr-2" />
            Mark All as Read
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notifications
            {unreadCount > 0 && (
              <Badge variant="default" className="ml-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="whats-new" className="flex items-center gap-2">
            <Rocket className="h-4 w-4" />
            What's New
            {unreadReleases.length > 0 && (
              <Badge variant="secondary" className="ml-1 bg-emerald-500 text-white">
                {unreadReleases.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="mt-4">
          <NotificationsTab
            notifications={notifications}
            loading={loading}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            moduleFilter={moduleFilter}
            setModuleFilter={setModuleFilter}
            unreadCount={unreadCount}
            hasMore={hasMore}
            page={page}
            setPage={setPage}
            handleClick={handleClick}
            handleMarkAsRead={handleMarkAsRead}
            handleArchive={handleArchive}
            handleDelete={handleDelete}
            fetchNotifications={fetchNotifications}
          />
        </TabsContent>

        <TabsContent value="whats-new" className="mt-4">
          <WhatsNewTab 
            releases={releases} 
            loading={releasesLoading} 
            unreadReleases={unreadReleases}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Notifications Tab Component
function NotificationsTab({
  notifications,
  loading,
  statusFilter,
  setStatusFilter,
  moduleFilter,
  setModuleFilter,
  hasMore,
  setPage,
  handleClick,
  handleMarkAsRead,
  handleArchive,
  handleDelete,
  fetchNotifications
}) {
  return (
    <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="read">Read</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                <SelectItem value="travel">Travel</SelectItem>
                <SelectItem value="timesheet">Timesheet</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
                <SelectItem value="leave">Leave</SelectItem>
                <SelectItem value="payroll">Payroll</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading && notifications.length === 0 ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
              <Bell className="h-16 w-16 mb-4 text-muted-foreground/30" />
              <p className="text-lg">No notifications</p>
              <p className="text-sm">You&apos;re all caught up!</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((n) => (
                <div
                  key={n.id}
                  className={`p-4 hover:bg-muted/50 transition-colors ${n.status === 'unread' ? 'bg-primary/5' : ''}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-2xl">{moduleIcons[n.module] || '📢'}</div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleClick(n)}>
                      <div className="flex items-center gap-2 mb-1">
                        <p className={`text-sm ${n.status === 'unread' ? 'font-semibold' : ''}`}>
                          {n.title}
                        </p>
                        <Badge className={priorityColors[n.priority]}>
                          {n.priority}
                        </Badge>
                        {n.status === 'unread' && (
                          <span className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{n.message}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {format(new Date(n.created_at), 'PPp')}
                        {' · '}
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      {n.status === 'unread' && (
                        <Button variant="ghost" size="icon" onClick={() => handleMarkAsRead([n.id])} title="Mark as read">
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => handleArchive([n.id])} title="Archive">
                        <Archive className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(n.id)} title="Delete">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {hasMore && (
                <div className="p-4 text-center">
                  <Button variant="outline" onClick={() => { setPage(p => p + 1); fetchNotifications(); }} disabled={loading}>
                    {loading ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
}

// What's New Tab Component
function WhatsNewTab({ releases, loading, unreadReleases }) {
  const unreadIds = new Set(unreadReleases.map(r => r.id));
  
  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (releases.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-12 text-muted-foreground">
          <Rocket className="h-16 w-16 mb-4 text-muted-foreground/30" />
          <p className="text-lg">No releases yet</p>
          <p className="text-sm">New features and updates will appear here</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {releases.map((release) => (
        <Card key={release.id} className={`relative overflow-hidden ${unreadIds.has(release.id) ? 'ring-2 ring-emerald-500' : ''}`}>
          {unreadIds.has(release.id) && (
            <div className="absolute top-0 left-0 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-br font-medium">
              NEW
            </div>
          )}
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Rocket className="h-5 w-5 text-primary" />
                  {release.title}
                </CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="font-mono">v{release.version}</Badge>
                  <span>•</span>
                  <span>{format(new Date(release.created_at), 'MMMM d, yyyy')}</span>
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{release.description}</p>
            
            {/* Features */}
            {release.features?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-emerald-600 flex items-center gap-1 mb-2">
                  <Sparkles className="h-4 w-4" />
                  New Features
                </h4>
                <ul className="space-y-1">
                  {release.features.map((feature, idx) => (
                    <li key={idx} className="text-sm flex items-start gap-2">
                      <ChevronRight className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Improvements */}
            {release.improvements?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-blue-600 flex items-center gap-1 mb-2">
                  <Wrench className="h-4 w-4" />
                  Improvements
                </h4>
                <ul className="space-y-1">
                  {release.improvements.map((improvement, idx) => (
                    <li key={idx} className="text-sm flex items-start gap-2">
                      <ChevronRight className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                      <span>{improvement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Bug Fixes */}
            {release.bug_fixes?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-red-600 flex items-center gap-1 mb-2">
                  <Bug className="h-4 w-4" />
                  Bug Fixes
                </h4>
                <ul className="space-y-1">
                  {release.bug_fixes.map((fix, idx) => (
                    <li key={idx} className="text-sm flex items-start gap-2">
                      <ChevronRight className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                      <span>{fix}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
